import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowsOut, Sparkle, Warning, CaretRight } from "@phosphor-icons/react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { env } from "../../lib/env";
import { photoForRoom } from "../../data/imagery";
import { cls } from "../../lib/cls";
import { RoomDetailSheet } from "./RoomDetailSheet";

function RoomPhoto({ label, id }: { label: string; id: string }): JSX.Element {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(135deg, #FCE6D2, #E8B591)` }}
        aria-hidden
      />
    );
  }
  return (
    <img
      src={photoForRoom(label)}
      alt={label}
      loading="lazy"
      onError={() => setFailed(true)}
      data-room={id}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
}

export function RoomsView(): JSX.Element {
  // Re-read active graph on every project switch so rooms reflect the
  // chosen project, not a baked-in singleton.
  const [graph, setGraph] = useState(() => getActiveProjectGraph());
  const [openRoomId, setOpenRoomId] = useState<string | null>(null);

  useEffect(() => {
    function refresh() { setGraph(getActiveProjectGraph()); }
    window.addEventListener("foyrmind:projectChanged", refresh);
    return () => window.removeEventListener("foyrmind:projectChanged", refresh);
  }, []);

  const rooms = graph.nodes.filter((n) => n.kind === "Room");
  const skusByRoom = new Map<string, string[]>();
  for (const edge of graph.edges) {
    if (edge.kind === "IN_ROOM") {
      const list = skusByRoom.get(edge.target) ?? [];
      list.push(edge.source);
      skusByRoom.set(edge.target, list);
    }
  }

  function handleRender(roomId: string) {
    // Hand off to the cockpit Render tab with the room pre-bound.
    try { window.localStorage.setItem(env.VITE_LS_RENDER_ROOM, roomId); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("foyrmind:openRenderRoom", { detail: { roomId } }));
    setOpenRoomId(null);
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {rooms.map((room, i) => {
          const skuIds = skusByRoom.get(room.id) ?? [];
          const skus = graph.nodes.filter((n) => skuIds.includes(n.id));
          const longLead = skus.find((s) => Number(s.attrs["leadDays"] ?? 0) > 60);
          const area = room.attrs["area"];
          const ceilingHt = room.attrs["ceilingHt"];
          return (
            <motion.article
              key={room.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="panel overflow-hidden group"
            >
              <button
                type="button"
                onClick={() => setOpenRoomId(room.id)}
                aria-label={`Open ${room.label} detail`}
                className="relative aspect-[16/9] overflow-hidden w-full text-left cursor-pointer"
              >
                <RoomPhoto label={room.label} id={room.id} />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-ink-950/10 to-transparent pointer-events-none" />

                <div className="absolute top-3 left-3 flex flex-col gap-1">
                  <span className="pill bg-cream/90 text-ink-950 backdrop-blur-sm">
                    {area ? `${area} sq ft` : "—"} {ceilingHt ? `· ${ceilingHt} ft H` : ""}
                  </span>
                  {longLead ? (
                    <span className="pill bg-foyr text-cream">
                      <Warning weight="duotone" className="w-3 h-3" /> long-lead SKU
                    </span>
                  ) : null}
                </div>

                <div className="absolute bottom-3 left-4 right-4 text-cream">
                  <div className="text-[11px] uppercase tracking-wider opacity-80">Room</div>
                  <div className="display-3 leading-tight">{room.label}</div>
                </div>

                <span className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-cream/90 grid place-items-center text-ink-950 backdrop-blur-sm group-hover:bg-cream transition">
                  <ArrowsOut weight="bold" className="w-4 h-4" />
                </span>
              </button>

              <div className="p-3 sm:p-4 space-y-2">
                <div className="flex items-center justify-between text-[11px] text-ink-500">
                  <span className="eyebrow">In this room</span>
                  <span className="tabular">{skus.length} {skus.length === 1 ? "item" : "items"}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {skus.map((s) => {
                    const lead = Number(s.attrs["leadDays"] ?? 0);
                    const flagged = lead > 60;
                    return (
                      <span
                        key={s.id}
                        className={cls(
                          "pill border",
                          flagged ? "bg-foyr/10 border-foyr/30 text-foyr-deep" : "bg-cream-dark border-border text-ink-700",
                        )}
                      >
                        {flagged ? <Sparkle weight="duotone" className="w-3 h-3" /> : null}
                        {s.label}
                      </span>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setOpenRoomId(room.id)}
                  className="w-full mt-1 btn btn-outline !text-xs !py-1.5 justify-between"
                >
                  Open room detail <CaretRight weight="bold" className="w-3 h-3" />
                </button>
              </div>
            </motion.article>
          );
        })}
      </div>

      {openRoomId ? (
        <RoomDetailSheet
          graph={graph}
          roomId={openRoomId}
          onClose={() => setOpenRoomId(null)}
          onRender={handleRender}
        />
      ) : null}
    </>
  );
}
